/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import moment from 'moment';
import { IArraySeries } from '../../DashboardKPIWidgets/EnergyOptimization/EnergyOptimizationInterfaces';
import { MAGIC_VALUE } from './Constants';
import { TimeInterval } from './types';

const startOfDayUnixMilliseconds = (input: string | number) =>
  moment(input)
    .startOf('day')
    .valueOf();

const endOfDayUnixMilliseconds = (input: string | number) =>
  moment(input)
    .endOf('day')
    .valueOf();

export const getStartTickPosition = (
  startTime?: string,
  chartSeries?: IArraySeries
) => {
  if (startTime) {
    return startOfDayUnixMilliseconds(startTime);
  } else {
    return chartSeries && startOfDayUnixMilliseconds(chartSeries[0][0]);
  }
};

export const getEndTickPosition = (
  endTime?: string,
  chartSeries?: IArraySeries,
  isAllUtility?: boolean
) => {
  if (endTime) {
    return isAllUtility &&
      chartSeries?.length === 2 &&
      chartSeries[0][1] === chartSeries[1][1]
      ? endOfDayUnixMilliseconds(endTime.split('T')[0])
      : isAllUtility
      ? startOfDayUnixMilliseconds(endTime.split('T')[0])
      : startOfDayUnixMilliseconds(endTime);
  } else {
    return (
      chartSeries &&
      startOfDayUnixMilliseconds(chartSeries[chartSeries.length - 1][0])
    );
  }
};

export const getEndTickPositionForSingleDay = (
  endTime?: string,
  chartSeries?: IArraySeries
) => {
  if (endTime) {
    return endOfDayUnixMilliseconds(endTime);
  } else {
    return (
      chartSeries &&
      endOfDayUnixMilliseconds(chartSeries[chartSeries.length - 1][0])
    );
  }
};

const getMonthlyTickPositions = (
  axisMin: moment.Moment,
  axisMax: moment.Moment,
  axisPixelLength: number
) => {
  const tickPositions = [];
  const daysDiff = axisMax.diff(axisMin, 'days');

  let intervalInside = 1;
  if (axisPixelLength <= MAGIC_VALUE.EIGHTFIVTY) {
    intervalInside = MAGIC_VALUE.TWO;
  }

  let interval = intervalInside;
  if (axisPixelLength <= MAGIC_VALUE.SIXHUND) {
    interval = MAGIC_VALUE.THREE;
  }

  if (daysDiff === 0) {
    // show the label at middle of axis for single day.
    tickPositions.push(
      moment(axisMin)
        .add(MAGIC_VALUE.TWELVE, 'hours')
        .valueOf()
    );
  } else {
    for (let i = 0; i <= daysDiff; i += interval) {
      tickPositions.push(
        moment(axisMin)
          .add(i, 'day')
          .valueOf()
      );
    }
  }
  return tickPositions;
};

const getTickPositionsOn15thOfMonth = (
  axisMin: moment.Moment,
  axisMax: moment.Moment
) => {
  const tickPositions = [];
  const monthsDiff = axisMax.diff(axisMin, 'months');
  for (let i = 0; i <= monthsDiff; i++) {
    tickPositions.push(
      moment(axisMin)
        .date(MAGIC_VALUE.FIFTEEN)
        .add(i, 'month')
        .valueOf()
    );
  }
  return tickPositions;
};

export default (
  interval: TimeInterval,
  axisMin: moment.Moment,
  axisMax: moment.Moment,
  axisPixelLength: number
) => {
  if (axisMin === undefined || axisMax === undefined) {
    return [];
  }
  if (interval === TimeInterval.Monthly) {
    return getMonthlyTickPositions(axisMin, axisMax, axisPixelLength);
  } else if (
    interval === TimeInterval.ThreeMonths ||
    interval === TimeInterval.Yearly
  ) {
    return getTickPositionsOn15thOfMonth(axisMin, axisMax);
  } else {
    return [];
  }
};
